import { getDefault } from '@/App/Service/Setting/Registry';
import Store from '../Store';
import Vue, { VueConstructor } from 'vue';

/**
 * Setting helper interface
 */
interface ISettingHelper {
	(property: string, namespace?: string): any;
}

/**
 * Vue Plugin for Settings System
 *
 * Provides global $setting property to all Vue components for easy access
 * to server-configurable settings with local defaults.
 *
 * Usage:
 *   $setting('maxGalleryImages', 'venue')  -> looks up 'venue_maxGalleryImages'
 *   $setting('venue_maxGalleryImages')     -> direct key lookup
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Plugin
 * @project ChalkySticks Web App
 */
const SettingPlugin = {
	/**
	 * Install the plugin
	 *
	 * @param Vue _Vue
	 * @param object options
	 * @return void
	 */
	install(_Vue: VueConstructor, options?: any): void {
		/**
		 * Get a setting value with optional namespace
		 *
		 * @param string property
		 * @param string namespace
		 * @return any
		 */
		const settingHelper: ISettingHelper = (property: string, namespace?: string): any => {
			const key = namespace ? `${namespace}_${property}` : property;
			const storeValue = Store.state.settings?.appSettings?.[key];

			return storeValue !== undefined ? storeValue : getDefault(key);
		};

		// Define the property on the Vue prototype
		Object.defineProperty(_Vue.prototype, '$setting', {
			/**
			 * @return ISettingHelper
			 */
			get(): ISettingHelper {
				return settingHelper;
			},
		});

		// Also add as a global mixin for computed properties if needed
		_Vue.mixin({
			/**
			 * Computed properties available to all components
			 */
			computed: {
				/**
				 * Access to all settings as an object (merged with defaults)
				 *
				 * @return Record<string, any>
				 */
				$settings(): Record<string, any> {
					return Store.getters['settings/appSettings'];
				},
			},
		});
	},
};

// Module augmentation to add $setting to Vue instance type
declare module 'vue/types/vue' {
	interface Vue {
		$setting: ISettingHelper;
		$settings: Record<string, any>;
	}
}

export default SettingPlugin;
